import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { User } from 'model';
import { fetchLike as fetch } from '../api/baseQueryWithReauth.js';

export interface RegistrationState {
	username: string;
	email: string;
	password: string;
	passwordConfirm: string;
	registered: 'failed' | 'registered' | 'registering' | 'verified' | 'verifying' | null;
	errorMessage: string;
}

export const register = createAsyncThunk(
	'registration/create',
	async ({
		email,
		username,
		password
	}: {
		email: string;
		password: string;
		username: string;
	}): Promise<{ token: string; user: User }> => {
		const response = await fetch('/api/users/create', {
			method: 'POST',
			cache: 'no-cache',
			headers: {
				'Content-Type': 'application/json'
			},
			body: JSON.stringify({ email, username, password })
		});
		if (response.status === 201) {
			return await response.json();
		} else {
			const error = await response.json();
			throw new Error(error);
		}
	}
);

export const resend = createAsyncThunk(
	'registration/resend',
	async ({ email }: { email: string }): Promise<{ email: string; username: User }> => {
		const response = await fetch('/api/users/resend/' + email, {
			method: 'GET',
			cache: 'no-cache',
			headers: {
				'Content-Type': 'application/json'
			}
		});
		if (response.status === 200) {
			return await response.json();
		} else {
			const error = await response.json();
			throw new Error(error);
		}
	}
);

export const verify = createAsyncThunk(
	'registration/verify',
	async ({ token }: { token: string }): Promise<{ token: string; user: User }> => {
		const response = await fetch('/api/users/register/' + token, {
			method: 'GET',
			cache: 'no-cache',
			headers: {
				'Content-Type': 'application/json'
			}
		});
		if (response.status === 200) {
			return await response.json();
		} else {
			const error = await response.json();
			throw new Error(error);
		}
	}
);

export const registrationSlice = createSlice({
	name: 'registration',
	initialState: {
		username: '',
		email: '',
		password: '',
		passwordConfirm: '',
		registered: null,
		errorMessage: ''
	} as RegistrationState,
	reducers: {
		setEmail: (state, { payload }: { payload: string | null | undefined }) => {
			state.email = payload ?? '';
		},
		setPassword: (state, action) => {
			state.password = action.payload;
		},
		setPasswordConfirm: (state, action) => {
			state.passwordConfirm = action.payload;
		},
		setUsername: (state, action) => {
			state.username = action.payload;
		},
		setErrorMessage: (state, action) => {
			state.errorMessage = action.payload;
		},
		setRegistered: (state, action: { payload: RegistrationState['registered'] }) => {
			state.registered = action.payload;
		}
	},
	extraReducers: builder => {
		// Add reducers for additional action types here, and handle loading state as needed
		builder.addCase(register.fulfilled, (state, action) => {
			// Add user to the state array
			state.registered = 'registered';
		});
		builder.addCase(register.rejected, (state, action) => {
			state.registered = 'failed';
			state.errorMessage = action.error.message ?? '';
		});
		builder.addCase(register.pending, state => {
			state.registered = 'registering';
		});
		builder.addCase(resend.fulfilled, (state, action) => {
			// Add user to the state array
			state.registered = 'registered';
		});
		builder.addCase(resend.rejected, (state, action) => {
			state.registered = 'failed';
			state.errorMessage = action.error.message ?? '';
		});
		builder.addCase(resend.pending, state => {
			state.registered = 'registering';
		});
		builder.addCase(verify.fulfilled, (state, action) => {
			// Add user to the state array
			state.registered = 'verified';
		});
		builder.addCase(verify.rejected, (state, action) => {
			state.registered = 'failed';
			state.errorMessage = action.error.message ?? '';
		});
		builder.addCase(verify.pending, state => {
			state.registered = 'verifying';
		});
	}
});

// Action creators are generated for each case reducer function
export const { setEmail, setPassword, setErrorMessage, setPasswordConfirm, setUsername, setRegistered } =
	registrationSlice.actions;
export default registrationSlice.reducer;
